import { useMemo, useState, useEffect, useContext } from "react"
import {
    Loader2, Mountain, MessageCircleQuestion, MessageCircleMore, CheckCircle2, Plus, Trash2, Redo2
} from "lucide-react"
import LessonGeneratePanel from "../components/LessonGeneratePanel"
import CommonSettings from "@/widgets/CommonSettings"
import { LIMIT_AI_TOKEN } from "@/settings/master"
import { generateExam, listAttachments as listAttachmentsApi, fetchExamBank, saveQuestions, updateQuestions, deleteQuestions } from "@/api/kuizy"
import { useParams, useNavigate } from "react-router-dom"
import {
    levelMapping,
    examTypeMapping,
    typeSummaryMapping,
    LANGUAGES_AI,
    localeToLabel,
    browserLocale
} from '@/settings/master'
import { motion, AnimatePresence } from "framer-motion"
import QuestionsCard from '@/widgets/QuestionsCard'
import ConfirmModal from '@/widgets/ConfirmModal'
import ManualQuestionForm from '@/widgets/ManualQuestionForm'
import { LayoutContext } from "@/layout/KuizyLayout"

export default function LessonExamBuilder({
    lessonId,
    files = [],
}) {
    const nav = useNavigate()
    const { showLoading, hideLoading } = useContext(LayoutContext) || {}
    const initLang = () => {
        const label = localeToLabel(browserLocale || 'th')
        const e = LANGUAGES_AI.find(l => l.label === label)
        return e ? e.name : 'Thai'
    }
    const { cid } = useParams()
    const [selectedFileIds, setSelectedFileIds] = useState([])
    const [generating, setGenerating] = useState(false)
    const [uploadBusy, setUploadBusy] = useState(false)

    const [extraPrompt, setExtraPrompt] = useState("")
    const [examType, setExamType] = useState('multipleChoice')
    const [optionCount, setOptionCount] = useState(4)
    const [level, setLevel] = useState("2")
    const [questionCount, setQuestionCount] = useState(10)
    const [optionStyle, setOptionStyle] = useState('thai')
    const [typeSummary, setTypeSummary] = useState('TF')
    const [typeSummaryMin, setTypeSummaryMin] = useState(0)
    const [typeSummaryMax, setTypeSummaryMax] = useState(0)
    const [examLanguage, setExamLanguage] = useState(initLang)
    const [listAttachments, setListAttachmets] = useState([])

    const [step, setStep] = useState(1)
    const fadeUp = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.35 } }

    const [questions, setQuestions] = useState([])
    const [saving, setSaving] = useState(false)

    const uid = localStorage.getItem('userId') || '1'
    const [selectedNew, setSelectedNew] = useState(new Set())
    const [selectedOld, setSelectedOld] = useState(new Set())
    const [confirmState, setConfirmState] = useState(null)
    const [showManualPopup, setShowManualPopup] = useState(false)
    const [manualOptions, setManualOptions] = useState(Array(optionCount).fill(0).map(() => ({ desc: '', points: 1 })))
    const [manualCorrect, setManualCorrect] = useState([])
    const [newQuestionText, setNewQuestionText] = useState('')
    const [editingIndex, setEditingIndex] = useState(null)
    const [editedQuestion, setEditedQuestion] = useState(null)

    useEffect(() => {
        setManualOptions(Array(optionCount).fill(0).map(() => ({ desc: '', points: 1 })))
        setManualCorrect([])
    }, [optionCount])

    const [confirmRe, setConfirmRe] = useState(false)

    useEffect(() => {
        fetchExamBank({
            content_id: cid,
            lesson_id: lessonId, page: 1, pageSize: 'all'
        })
            .then(({ data }) => {
                const list = (data?.data || []).map(q => ({ ...q, _isNew: false }))
                setQuestions(list)
                if (list.length) {
                    const optLen = Array.isArray(list[0].answer)
                        ? list[0].answer.length
                        : (typeof list[0].answer === 'object' ? Object.keys(list[0].answer || {}).length : 0)
                    setOptionCount(optLen || 4)
                    setLevel(String(list[0].difficult_level || level))
                    setTypeSummary(list[0].type_summary || typeSummary)
                    setExamType(list[0].exam_type || examType)
                    setOptionStyle(list[0].option_style || optionStyle)
                }
            })
            .catch(() => alert('โหลดข้อสอบไม่สำเร็จ'))

        fetchExamBank();
    }, [lessonId])
    const totalToken = useMemo(() => {
        return files
            .filter(f => selectedFileIds.includes(f.id))
            .reduce((sum, f) => sum + (parseInt(f.token, 10) || 0), 0)
    }, [files, selectedFileIds])

    const overLimit = totalToken / 100 > LIMIT_AI_TOKEN

    const fetchAttachments = async () => {
        const { data } = await listAttachmentsApi(cid)

        setListAttachmets(data?.data || [])
    }

    useEffect(() => {
        fetchAttachments()
    }, [cid])

    const handleGenerateClick = async () => {
        if (!selectedFileIds.length || overLimit) return

        try {
            setGenerating(true)
            if (showLoading) {
                showLoading({
                    variant: 'imageSplit',
                    text: 'กำลังสร้างข้อสอบ...',
                    subtext: 'ประมวลผลด้วย AI กรุณารอสักครู่',
                    imageUrl: '/images/write-exam-2.gif',
                })
            }

            const { data } = await generateExam({
                contentCode: cid,
                attachment_ids: selectedFileIds,
                question: questionCount,
                option: optionCount,
                level,
                examType: examType || null,       // 🔥 กัน ""
                typeSummary: typeSummary || null, // 🔥 เพิ่ม
                language: examLanguage,
                prompt: extraPrompt,
                shuffle_options: true
            })

            const ready = (Array.isArray(data) ? data : []).map((q, idx) => ({
                id: q.id,
                lesson_id: q.lesson_id || q.lessonIds?.[0] || lessonId,
                content_id: cid,
                difficult_level: q.difficult_level || level,
                question: q.question,
                answer: q.answer,
                correct: q.correct,
                createdAt: new Date().toISOString(),
                _isNew: true
            }))
            setQuestions(prev => [...ready, ...prev])
            setStep(2)
        } finally {
            setGenerating(false)
            setStep(2)
            if (hideLoading) hideLoading()
        }
    }


    const { newQs, oldQs, newMap, oldMap } = questions.reduce(
        (acc, q, idx) => {
            if (q._isNew) {
                acc.newQs.push(q)
                acc.newMap.push(idx)
            } else {
                acc.oldQs.push(q)
                acc.oldMap.push(idx)
            }
            return acc
        },
        { newQs: [], oldQs: [], newMap: [], oldMap: [] }
    )

    const handleRegenerate = () => {
        setSelectedFileIds([])
    }

    const addManualQuestion = () => {
        if (!newQuestionText.trim()) { alert('กรุณาป้อนคำถาม'); return }
        if (manualOptions.some(o => !o.desc.trim())) { alert('กรุณาป้อนตัวเลือกให้ครบ'); return }
        if (manualCorrect.length === 0 && typeSummary !== 'PO') { alert('เลือกคำตอบถูกต้องอย่างน้อย 1 ค่า'); return }
        const answerObjArr = manualOptions.map((o, idx) => ({
            [`option${idx + 1}`]: typeSummary === 'PO'
                ? { description: o.desc, points: o.points }
                : o.desc
        }))
        const newQ = {
            lesson_id: lessonId,
            content_id: cid,
            question: newQuestionText,
            answer: answerObjArr,
            correct: [...manualCorrect],
            difficult_level: level,
            created_by: uid,
            createdAt: new Date().toISOString(),
            _isNew: true
        }
        setQuestions(p => [newQ, ...p])
        setShowManualPopup(false)
        setNewQuestionText('')
        setManualOptions(Array(optionCount).fill(0).map(() => ({ desc: '', points: 1 })))
        setManualCorrect([])
    }

    const saveToDB = async () => {
        if (saving) return
        // if (!lessonId) { alert('ไม่พบรหัสบทเรียน (lesson_id)'); return }
        setSaving(true)
        const qPayload = questions.map(q => ({
            id: q.id,
            lesson_id: q.lesson_id || q.lessonIds?.[0],
            content_id: cid,
            question: q.question,
            answer: q.answer,
            correct: q.correct,
            difficult_level: q.difficult_level || level,
            // created_by: q.created_by || uid
        }))

        try {
            if (questions.some(q => !q._isNew)) {
                await updateQuestions({}, qPayload)
            } else {
                await saveQuestions({ questions: qPayload })
            }
            nav(`/content/${cid}`, { replace: true })
        } catch (e) {
            alert(e.message || 'บันทึกไม่สำเร็จ')
        } finally {
            setSaving(false)
        }
    }


    const makeCardProps = idxMap => ({
        levelMapping,
        optionStyle,
        typeSummary,
        editingIndex: idxMap.indexOf(editingIndex),
        setEditingIndex: localIdx => {
            if (localIdx === null || localIdx === -1) { setEditingIndex(null); setEditedQuestion(null); return }
            const gIdx = idxMap[localIdx]
            if (gIdx !== undefined) openEditGlobal(gIdx)
        },
        editedQuestion,
        setEditedQuestion,
        startEditQuestion: localIdx => openEditGlobal(idxMap[localIdx]),
        handleSaveEdit,
        handleDeleteQuestion: localIdx => handleDeleteGlobal(idxMap[localIdx]),
        openAlert: alert
    })

    const confirmDeleteSelectedNew = () => {
        setConfirmState({
            title: 'ยืนยันการลบ',
            message: `ต้องการลบข้อสอบใหม่ที่เลือก ${selectedNew.size} ข้อ ใช่หรือไม่ ?`,
            onConfirm: async () => {
                setQuestions(prev => {
                    const toDelete = new Set([...selectedNew].map(i => newMap[i]))
                    return prev.filter((_, idx) => !toDelete.has(idx))
                })

                setSelectedNew(new Set())
                setEditingIndex(null)
                setEditedQuestion(null)
                closeConfirm()
            }
        })
    }

    const confirmDeleteAllNew = () => {
        setConfirmState({
            title: 'ยืนยันการลบทั้งหมด',
            message: `ต้องการลบข้อสอบใหม่ทั้งหมด ${newQs.length} ข้อ ใช่หรือไม่ ?`,
            onConfirm: () => {
                setQuestions(prev => prev.filter(q => !q._isNew))
                setSelectedNew(new Set())
                setEditingIndex(null)
                setEditedQuestion(null)
                closeConfirm()
            }
        })
    }

    const confirmDeleteSelectedOld = () => {
        const ids = [...selectedOld].map(i => oldQs[i].id)

        setConfirmState({
            title: 'ยืนยันการลบข้อสอบ',
            message: (
                <div>
                    <p>ต้องการลบข้อสอบเดิมที่เลือก {ids.length} ข้อ ใช่หรือไม่</p>
                    <p className="text-xs text-red-500 mt-1">
                        * ข้อสอบที่ลบแล้วไม่สามารถกู้คืนได้
                    </p>
                </div>
            ),
            onConfirm: async () => {
                await deleteQuestions(ids)

                setQuestions(prev => prev.filter(q => !ids.includes(q.id)))
                setSelectedOld(new Set())
                setEditingIndex(null)
                setEditedQuestion(null)
                closeConfirm()
            }
        })
    }

    const confirmDeleteAllOld = () => {
        const ids = oldQs.map(q => q.id)

        setConfirmState({
            title: 'ยืนยันการลบข้อสอบทั้งหมด',
            message: (
                <div>
                    <p>ต้องการลบข้อสอบเดิมทั้งหมด {ids.length} ข้อ ใช่หรือไม่</p>
                    <p className="text-xs text-red-600 mt-1 font-semibold">
                        การลบนี้มีผลกับข้อมูลจริง
                    </p>
                </div>
            ),
            onConfirm: async () => {
                await deleteQuestions(ids)

                setQuestions(prev => prev.filter(q => q._isNew))
                setSelectedOld(new Set())
                setEditingIndex(null)
                setEditedQuestion(null)
                closeConfirm()
            }
        })
    }

    const toggleSelectNew = (idx) => {
        setSelectedNew(prev => {
            const next = new Set(prev)
            if (next.has(idx)) next.delete(idx)
            else next.add(idx)
            return next
        })
    }

    const toggleSelectOld = (idx) => {
        setSelectedOld(prev => {
            const next = new Set(prev)
            if (next.has(idx)) next.delete(idx)
            else next.add(idx)
            return next
        })
    }

    const openEditGlobal = gIdx => {
        setEditingIndex(gIdx)
        setEditedQuestion({ ...questions[gIdx] })
    }
    const handleSaveEdit = () => {
        if (!editedQuestion) return
        const cp = [...questions]
        cp[editingIndex] = { ...editedQuestion, updatedAt: new Date().toISOString() }
        setQuestions(cp)
        setEditingIndex(null)
        setEditedQuestion(null)
    }

    const genSummary = () => {
        const langLabel = LANGUAGES_AI.find(l => l.name === examLanguage)?.label || examLanguage
        return (
            <ul className="list-none p-0 m-0 text-sm space-y-2">
                <li><b>ภาษา:</b> <span className="font-bold text-red-500">{langLabel}</span></li>
                <li><b>ระดับความยาก:</b> {levelMapping[level]}</li>
                <li><b>ประเภทข้อสอบ:</b> {examTypeMapping[examType]}</li>
                <li><b>จำนวนข้อสอบ:</b> {questionCount} ข้อ</li>
                <li><b>จำนวนคำตอบ:</b> {optionCount} ตัวเลือก</li>
                <li><b>ประเภทคำตอบ:</b> {typeSummaryMapping[typeSummary]}</li>
            </ul>
        )
    }

    return (
        <>
            <AnimatePresence mode="wait">
                {step === 1 && (
                    <motion.div
                        key="step1"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                    >
                        <div className="flex flex-col gap-8">

                            <LessonGeneratePanel
                                lessons={listAttachments}
                                selected={selectedFileIds}
                                setSelected={setSelectedFileIds}
                            />

                            {overLimit && (
                                <div className="text-sm text-red-500">
                                    ⚠ token รวมเกิน limit ({LIMIT_AI_TOKEN})
                                </div>
                            )}

                            <div className="grid md:grid-cols-3 gap-4">
                                <div>
                                    <label className="flex items-center gap-1 text-sm"><Mountain size={16} /> ระดับความยาก</label>
                                    <div className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed">
                                        {levelMapping[level]}
                                    </div>
                                </div>
                                <div>
                                    <label className="flex items-center gap-1 text-sm"><MessageCircleQuestion size={16} /> ประเภทข้อสอบ</label>
                                    <div className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed">
                                        {examTypeMapping[examType]}
                                    </div>
                                </div>
                                <div>
                                    <label className="flex items-center gap-1 text-sm"><MessageCircleMore size={16} /> ประเภทคำตอบ</label>
                                    <div className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-100 text-gray-500 cursor-not-allowed">
                                        {typeSummaryMapping[typeSummary]}
                                    </div>
                                </div>
                            </div>
                            <CommonSettings
                                generateButtonDisabled={
                                    uploadBusy ||
                                    generating ||
                                    !selectedFileIds.length ||
                                    overLimit
                                }
                                generateButtonLabel={
                                    generating ? (
                                        <span className="inline-flex items-center gap-2">
                                            <Loader2 className="animate-spin" size={16} />
                                            กำลังสร้าง…
                                        </span>
                                    ) : (
                                        "สร้างข้อสอบด้วย AI"
                                    )
                                }
                                onGenerateClick={handleGenerateClick}
                                showResetButton={false}
                                onResetClick={handleRegenerate}
                                KnowledgeUploaderEl={null}
                                extraPrompt={extraPrompt}
                                setExtraPrompt={setExtraPrompt}
                                examType={examType}
                                setExamType={setExamType}
                                optionCount={optionCount}
                                setOptionCount={setOptionCount}
                                level={level}
                                setLevel={setLevel}
                                optionStyle={optionStyle}
                                setOptionStyle={setOptionStyle}
                                questionCount={questionCount}
                                setQuestionCount={setQuestionCount}
                                typeSummary={typeSummary}
                                setTypeSummary={setTypeSummary}
                                typeSummaryMin={typeSummaryMin}
                                setTypeSummaryMin={setTypeSummaryMin}
                                typeSummaryMax={typeSummaryMax}
                                setTypeSummaryMax={setTypeSummaryMax}
                                levelMapping={levelMapping}
                                examTypeMapping={examTypeMapping}
                                typeSummaryMapping={typeSummaryMapping}
                                allowLanguage
                                examLanguage={examLanguage}
                                setExamLanguage={setExamLanguage}
                                langs={LANGUAGES_AI}
                            />

                        </div>
                    </motion.div>
                )}

                {step === 2 && (
                    <motion.div key="step2" {...fadeUp} className="bg-white rounded-xl shadow-md p-4 sm:p-6">
                        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                            <h2 className="text-lg font-semibold">3. จัดการข้อสอบทั้งหมด ({questions.length})</h2>
                            <button
                                onClick={() => setShowManualPopup(true)}
                                className="inline-flex items-center gap-2 bg-primary hover:bg-secondary text-white rounded-lg px-3 py-2 text-sm font-semibold"
                            >
                                <Plus size={16} /> เพิ่มข้อสอบด้วยตนเอง
                            </button>
                        </div>

                        {newQs.length > 0 && (
                            <>
                                <h3 className="text-primary border-b-2 border-primary inline-block pb-1 text-base font-semibold mb-2">
                                    ข้อสอบใหม่ ({newQs.length})
                                    {newQs.length > 0 && (
                                        <button
                                            className="ml-3 inline-flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white rounded-md px-2 py-1 text-xs"
                                            onClick={handleRegenerate}
                                        >
                                            <Redo2 size={14} /> สร้างใหม่
                                        </button>
                                    )}

                                    <button
                                        onClick={confirmDeleteSelectedNew}
                                        className="ml-3 inline-flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white rounded-md px-2 py-1 text-xs"
                                    >
                                        <Trash2 size={14} />ลบที่เลือก ({selectedNew.size})
                                    </button>

                                    <button
                                        onClick={confirmDeleteAllNew}
                                        className="ml-3 inline-flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white rounded-md px-2 py-1 text-xs"
                                    >
                                        <Trash2 size={14} />ลบทั้งหมด
                                    </button>
                                </h3>
                                <QuestionsCard
                                    questions={newQs}
                                    selectable
                                    selected={selectedNew}
                                    onToggleSelect={toggleSelectNew}
                                    onDeleteSelected={confirmDeleteSelectedNew}
                                    onDeleteAll={confirmDeleteAllNew}
                                    {...makeCardProps(newMap)}
                                />
                            </>
                        )}

                        {oldQs.length > 0 && (
                            <>
                                <h3 className="text-primary border-b-2 border-primary inline-block pb-1 text-base font-semibold mb-2">
                                    ข้อสอบเดิม ({oldQs.length})
                                    <button
                                        onClick={confirmDeleteSelectedOld}
                                        className="ml-3 inline-flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white rounded-md px-2 py-1 text-xs"
                                    >
                                        <Trash2 size={14} />ลบที่เลือก ({selectedOld.size})
                                    </button>

                                    <button
                                        onClick={confirmDeleteAllOld}
                                        className="ml-3 inline-flex items-center gap-1 bg-red-500 hover:bg-red-600 text-white rounded-md px-2 py-1 text-xs"
                                    >
                                        <Trash2 size={14} />ลบทั้งหมด
                                    </button>
                                </h3>
                                <QuestionsCard
                                    questions={oldQs}
                                    selectable
                                    selected={selectedOld}
                                    onToggleSelect={toggleSelectOld}
                                    onDeleteSelected={confirmDeleteSelectedOld}
                                    onDeleteAll={confirmDeleteAllOld}
                                    {...makeCardProps(oldMap)}
                                />

                            </>
                        )}

                        <div className="flex justify-end mt-6">
                            <button
                                disabled={saving}
                                onClick={saveToDB}
                                className="bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white rounded-lg px-5 py-2 text-sm font-semibold"
                            >
                                {saving ? 'กำลังบันทึก…' : 'บันทึกข้อสอบ'}
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showManualPopup && (
                    <motion.div
                        key="manual"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 flex items-center justify-center z-[999]"
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0, y: 20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 10 }}
                            transition={{ duration: 0.25 }}
                            className="bg-white rounded-xl w-full max-w-2xl p-6"
                        >
                            <h3 className="text-lg font-semibold mb-4">เพิ่มข้อสอบด้วยตนเอง</h3>
                            <ManualQuestionForm
                                typeSummary={typeSummary}
                                manualOptions={manualOptions}
                                setManualOptions={setManualOptions}
                                manualCorrect={manualCorrect}
                                setManualCorrect={setManualCorrect}
                                optionCount={optionCount}
                                typeSummaryMin={typeSummaryMin}
                                typeSummaryMax={typeSummaryMax}
                                newQuestionText={newQuestionText}
                                setNewQuestionText={setNewQuestionText}
                                addManualQuestion={addManualQuestion}
                                openAlert={alert}
                                hideAddButton
                            />
                            <div className="flex justify-end gap-3 mt-6">
                                <button
                                    className="bg-primary hover:bg-secondary text-white rounded-lg px-4 py-2 text-sm font-semibold"
                                    onClick={addManualQuestion}
                                >
                                    บันทึก
                                </button>
                                <button
                                    className="bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg px-4 py-2 text-sm font-semibold"
                                    onClick={() => setShowManualPopup(false)}
                                >
                                    ยกเลิก
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Confirm regenerate */}
            <AnimatePresence>
                {confirmRe && (
                    <ConfirmModal
                        type="confirm"
                        title="ยืนยันสร้างใหม่"
                        message="ต้องการสร้างข้อสอบชุดใหม่หรือไม่?"
                        onConfirm={handleGenerateClick}
                        onCancel={() => setConfirmRe(false)}
                    />
                )}
            </AnimatePresence>
        </>
    )
}
